<template>
  <!-- Don't set disabled, or else it will block interaction on the whole screen. Only close it when disabling -->
  <ion-menu :class="menuState" side="end" id="settings-menu" menu-id="settings" content-id="pane" swipe-gesture="false" @ionWillClose="menuState = 'closing'" @ionWillOpen="menuState = 'opening'" @ionDidClose="menuState = 'closed'" @ionDidOpen="menuState = 'open'">
    <ion-page>
      <ion-header>  
        <ion-toolbar class="user-info">
          <div class="welcome-header">
            <ion-icon :icon="darkModeActive ? logotype_dm : logotype_lm"></ion-icon>
            
            <ion-buttons>
              <LanguageMenu></LanguageMenu>
            </ion-buttons>
          </div>
        </ion-toolbar>
      </ion-header>
      <ion-content :fullscreen="true">
        <!-- Only enable minimum settings if not logged in -->
        <ion-list v-if="isUserPresent">
          <ion-item :detail="false" button @click="() => store.dispatch('auth/logout').catch(() => {})" class="logout-item">
            <ion-icon slot="start" :icon="person"></ion-icon>
            <ion-label><span class="wl-semibold">{{ userIdentification || '' }}</span><span>{{ i18n.$t('login.logout') }}</span></ion-label>
            <ion-icon slot="end" :icon="logOutOutline"></ion-icon>
          </ion-item>
        </ion-list>
        <ion-list v-if="openNotifications != null && openNotifications.length > 0">
          <ion-list-header class="notification-header">
            {{ i18n.$t("settings.notifications") }}
          </ion-list-header>
          <ion-item v-for="(notification, notificationIndex) in openNotifications" :key="notificationIndex">
            <ion-label class="notification-text">
              <h4 class="wl-semibold">{{ notification.title }}</h4>
              <h5 class="sub-text" v-if="notification.description != null">{{ notification.description }}</h5>
            </ion-label>
            <ProgressButton class="notification-button" slot="end" color="primary" spinner_name="crescent" fill="solid" @click="completeNotification(notificationIndex)" :disabled="isOtherNotificationCurrentlyCompleting(notificationIndex)" :loading="isCurrentlyCompleting(notificationIndex)">
              <span v-if="notification.action_text != null">{{ notification.action_text }}</span>
              <ion-icon v-else slot="icon-only" :icon="checkmarkOutline"></ion-icon>
            </ProgressButton>
          </ion-item>
        </ion-list>
        <ion-list>
          <ion-list-header>
            {{ i18n.$t("settings.settings") }}
          </ion-list-header>
          <ion-item lines="inset">
            <!-- Dark mode supports one of three states: AUTO (Use device preference), ON, OFF -->
            <ion-icon slot="start"
              :icon="darkModeActive ? moonOutline : sunnyOutline">
            </ion-icon>
            <ion-label class="dark-mode-label">{{ i18n.$t("settings.dark_mode") }}</ion-label>
            <ion-segment :value="`${darkModePreference}`" @ionChange="darkModePreference = $event.target.value" mode="ios" slot="end">
              <ion-segment-button value="true" layout="label-hide">
                <ion-icon :icon="moon"></ion-icon>
              </ion-segment-button>
              <ion-segment-button class="wl-semibold" value="undefined">
                <ion-label>A</ion-label>
              </ion-segment-button>
              <ion-segment-button value="false" layout="label-hide">
                <ion-icon :icon="sunny"></ion-icon>
              </ion-segment-button>
            </ion-segment>
          </ion-item>
          <ion-item v-if="isAuthenticated" button :detail="false" @click="showUploadStatus()">
            <ion-icon slot="start" :icon="cloudUploadOutline">
            </ion-icon>
            <ion-label>{{ i18n.$t("upload-status.title") }}</ion-label>
          </ion-item>
          <ion-list-header>
            {{ i18n.$t("settings.other") }}
          </ion-list-header>
          <ion-item v-if="store.getters['auth/hasPermission']('Tools')" button :detail="false" @click="navigateTo('/tools')">
            <ion-icon slot="start" :icon="constructOutline">
            </ion-icon>
            <ion-label>{{ i18n.$t("tools.title") }}</ion-label>
          </ion-item>
          <ion-item button :detail="false" @click="openLegalDocument('agb')">
            <ion-icon slot="start" :icon="readerOutline">
            </ion-icon>
            <ion-label>{{ i18n.$t("settings.agb") }}</ion-label>
          </ion-item>
          <ion-item button :detail="false" @click="openLegalDocument('avv')">
            <ion-icon slot="start" :icon="shieldCheckmarkOutline">
            </ion-icon>
            <ion-label>{{ i18n.$t("settings.avv") }}</ion-label>
          </ion-item>
          <ion-item button :detail="false" @click="openLegalDocument('privacy')">
            <ion-icon slot="start" :icon="fingerPrint">
            </ion-icon>
            <ion-label>{{ i18n.$t("settings.privacy") }}</ion-label>
          </ion-item>
          <ion-item button :detail="false" @click="openHrefAsModal(`${websiteBaseURL}impressum`, i18n.$t('settings.impressum'))">
            <ion-icon slot="start" :icon="information">
            </ion-icon>
            <ion-label>{{ i18n.$t("settings.impressum") }}</ion-label>
          </ion-item>
          <ion-list-header>
            {{ i18n.$t("settings.communication") }}
          </ion-list-header>
          <ion-item v-if="isAuthenticated" button :detail="false" @click="navigateTo('/general-feedback')">
            <ion-icon slot="start" :icon="chatboxEllipsesOutline">
            </ion-icon>
            <ion-label>{{ i18n.$t("general-feedback.title") }}</ion-label>
          </ion-item>
          <ion-item :detail="false" href="mailto:info@anirec.de">
            <ion-icon slot="start" :icon="at">
            </ion-icon>
            <ion-label>{{ i18n.$t("settings.contact") }}</ion-label>
          </ion-item>
        </ion-list>
        <div id="app-version-information">
          <a href="javascript:void(0);" @click="openHrefAsModal(`/licenses/licenses.html`, i18n.$t('settings.license.title'))">{{ i18n.$t('settings.license.title') }}</a>
          <a href="javascript:void(0);" @click="showLogs()"> {{ i18n.$t("settings.app_version") }}: {{ appVersion }} </a>
        </div>
      </ion-content>
    </ion-page>
  </ion-menu>
</template>

<script>
import { IonMenu, IonPage, IonHeader, IonContent, IonToolbar, IonList, IonListHeader, IonItem, IonSegment, IonSegmentButton, IonLabel, IonIcon, IonButtons, menuController } from '@ionic/vue';
import { defineComponent, computed, watch, ref } from 'vue';
import { useStore } from 'vuex';

import { moon, moonOutline, sunny, sunnyOutline, logOutOutline, constructOutline, readerOutline, fingerPrint, information, at, libraryOutline, cloudUploadOutline, chatboxEllipsesOutline, checkmarkOutline, shieldCheckmarkOutline, person } from 'ionicons/icons';

import { useI18n } from "@/utils/i18n";
import { useRouter } from 'vue-router';

import _ from 'lodash';

import logotype_lm from '@/assets/logos/logotype_lm.svg';
import logotype_dm from '@/assets/logos/logotype_dm.svg';

import LanguageMenu from '@/components/LanguageMenu.vue';

import ProgressButton from '@/components/ProgressButton.vue';

import { iosEnterAnimation, iosLeaveAnimation } from '@/utils/custom-transition/popover';

import {openIFrameModal, default as modalComponent} from '@/components/IFrameModal.vue';
import {openLegalConfirmationModal, default as legalConfirmationModalComponent} from '@/components/LegalConfirmationModal.vue';
import {openUploadStatusModal, default as uploadModalComponent} from '@/components/UploadStatusModal.vue';
import {openLogModal, default as logModalComponent} from '@/components/LogModal.vue';

export default defineComponent({
  name: 'SettingsMenu',
  props: {
    isAuthenticated: Boolean,
    isUserPresent: Boolean,
    userIdentification: String
  },
  components: {
    IonMenu, IonPage, IonHeader, IonContent, IonToolbar, IonList, IonListHeader, IonItem, IonSegment, IonSegmentButton, IonLabel, IonIcon, IonButtons, LanguageMenu, ProgressButton
  },
  setup(props){
    const store = useStore();

    const i18n = useI18n();

    const router = useRouter();

    const appVersion = process.env.VUE_APP_VERSION;

    const websiteBaseURL = process.env.VUE_APP_WEBSITE_URL;

    const menuState = ref(undefined);

    //Close the menu when authentication changes, because it results in navigation
    watch(
      () => props.isAuthenticated,
      () => {
        menuController.close('settings');
      }
    )

    //Only one notification can be completing at once!
    const currentlyCompletingNotification = ref(null);

    const TRANSLATED_NOTIFICATION_PROPERTIES = ['title', 'description', 'action_text'];

    const openNotifications = computed(() => {
      let notifications = store.getters['getNotifications'];

      notifications = _.map(notifications, (notification) => {
        //Translate only properties that should be translated, otherwise just take the original value
        return _.mapValues(notification, (notificationProperty, notificationPropertyKey) => {
          if (notification.translate === true && TRANSLATED_NOTIFICATION_PROPERTIES.includes(notificationPropertyKey)) {
            let translatedValue = i18n.$t(`notifications.${notificationProperty}`);
            if (translatedValue != null) return translatedValue;
          }
          //If no translation found, or the original value should be used, return it
          return notificationProperty;
        });
      })

      return notifications;
    });

    const completeNotification = async function(notificationIndex) {
      currentlyCompletingNotification.value = notificationIndex;
      await store.dispatch('completeNotification', notificationIndex);
      currentlyCompletingNotification.value = null;
    }

    const isCurrentlyCompleting = computed(() => function(notificationIndex) {
      return (currentlyCompletingNotification.value != null && notificationIndex == currentlyCompletingNotification.value);
    });

    const isOtherNotificationCurrentlyCompleting = computed(() => function(notificationIndex) {
      return (currentlyCompletingNotification.value != null && notificationIndex != currentlyCompletingNotification.value);
    });

    const darkModeActive = computed(() => store.getters.isDarkModeActive );
    const darkModePreference = computed({
      get: () => store.getters.isDarkModePreferred,
      set: newValue => {
        let setValue = undefined;
        if (newValue === true || newValue === false) {
          setValue = newValue;
        } else if (newValue === 'true') { //Catch string value as special case
          setValue = true;
        } else if (newValue === 'false') { //Catch string value as special case
          setValue = false;
        }
        store.dispatch('setDarkModePreference', setValue);
      }
    })

    const openHrefAsModal = function(href, title){
      menuController.close('settings');
      openIFrameModal(modalComponent, title, href);
    }

    const currentLegalVersions = computed(() => store.getters['auth/getConfirmedLegalVersions']);

    const openLegalDocument = function(type) {
      menuController.close('settings');
      let currentVersion = _.get(currentLegalVersions.value, [type, 'version']); //Try to get the type in the current versions of this user, otherwise load the latest one (set to undefined)
      openLegalConfirmationModal(legalConfirmationModalComponent, type, true, currentVersion);
    }

    const navigateTo = function(href){
      menuController.close('settings');
      router.push(href)
    }

    const showUploadStatus = function(){
      menuController.close('settings');
      openUploadStatusModal(uploadModalComponent);
    }

    const showLogs = function(){
      menuController.close('settings');
      openLogModal(logModalComponent);
    }

    return { iosEnterAnimation, iosLeaveAnimation, store, i18n, openNotifications, isCurrentlyCompleting, isOtherNotificationCurrentlyCompleting, completeNotification, darkModeActive, darkModePreference, appVersion, websiteBaseURL, menuState, moon, moonOutline, sunny, sunnyOutline, logOutOutline, constructOutline, readerOutline, fingerPrint, information, at, libraryOutline, cloudUploadOutline, chatboxEllipsesOutline, router, checkmarkOutline, shieldCheckmarkOutline, person, openHrefAsModal, openLegalDocument, navigateTo, showUploadStatus, showLogs, logotype_lm, logotype_dm }
  }
});
</script>

<style scoped>

#settings-menu {
  --height: auto;
  --width: min(90vw, 500px);
}

#settings-menu::part(backdrop)::before {
  content: "";
  background-image: url('/public/assets/close.svg');
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  margin: 10px;
  top: calc(50% - 1.5rem);
  left: var(--ion-safe-area-left, 0px);
}

.user-info {
  padding: 20px;
  padding-top: calc(var(--ion-safe-area-top, 0) + 20px)!important;
  padding-right: max(var(--ion-safe-area-right, 0), 20px)!important;
}

.welcome-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.welcome-header ion-icon {
  width: 100px;
  height: 24px;
}

ion-title {
  padding: 0px;
}

.login-prompt {
  cursor: pointer;
}

.login-prompt ion-icon {
  vertical-align: bottom;
  font-size: 1.5em;
}

ion-content {
  --background: var(--ion-item-background, var(--ion-background-color, #fff));
}

.logout-item ion-label {
  display: flex;
  justify-content: space-between;
}

ion-segment {
  --segment-count: 3;
  --segment-size: 48px;
  width: calc(var(--segment-count) * var(--segment-size));
  min-width: calc(var(--segment-count) * var(--segment-size));
  box-shadow: none;
  --background: transparent;
  border-style: none!important;
}

ion-segment-button {
  --color-checked: var(--ion-color-text);
  --padding-start: 0px;
  --padding-end: 0px;
  width: var(--segment-size);
  height: var(--segment-size);
  min-width: var(--segment-size);
  max-width: var(--segment-size);
}

ion-segment-button > * {
  font-size: 20px!important;
}

ion-segment-button::part(native) {
  min-width: 0px;
}

#app-version-information {
  margin-top: auto;
  height: fit-content;
  padding: 20px 10px;
  width: 100%;
  font-size: 0.8em;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

#app-version-information > * {
  text-transform: none;
  text-decoration: none;
  font-weight: var(--weight-level-light);
  flex-grow: 1;
  flex-basis: 50%;
  text-align: center;
}

#app-version-information > *:not(:last-child) {
  border-right: 1px solid var(--ion-border-color);
}

.notification-header {
  color: var(--ion-color-primary);
}

.notification-text {
  margin-left: 10px;
  white-space: normal!important;
}

.notification-text h2 {
  font-weight: 500;
}

.notification-text h3 {
  margin-top: 5px;
}

.notification-button {
  --custom-brightness: 0.8;
  height: 32px;
  filter: grayscale(calc((1 - var(--custom-brightness, 1)) * 100%));
}

ion-list {
  padding-top: 0px;
  padding-bottom: 0px;
}

ion-list-header {
  padding-top: 24px;
}

</style>
